// FCM 토큰 관리 및 브라우저 알림 권한 처리
import { getToken, onMessage } from 'firebase/messaging';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { messaging, db } from '../firebase';

// VAPID 키 (Firebase Console > 프로젝트 설정 > 클라우드 메시징 > 웹 푸시 인증서에서 생성)
// 나중에 .env에서 가져오도록 변경 예정
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

/**
 * 브라우저 알림 권한 요청
 * @returns {Promise<string>} 'granted', 'denied', 또는 'default'
 */
export const requestNotificationPermission = async () => {
  try {
    if (!('Notification' in window)) {
      console.log('이 브라우저는 알림을 지원하지 않습니다.');
      return 'unsupported';
    }

    const permission = await Notification.requestPermission();
    console.log('알림 권한:', permission);
    return permission;
  } catch (error) {
    console.error('알림 권한 요청 오류:', error);
    return 'error';
  }
};

/**
 * FCM 토큰 가져오기 및 Firestore에 저장
 * @param {string} userId - 사용자 이메일 또는 ID
 * @returns {Promise<string|null>} FCM 토큰 또는 null
 */
export const getFCMToken = async (userId) => {
  try {
    if (!messaging) {
      console.log('Firebase Messaging이 지원되지 않습니다.');
      return null;
    }

    // 알림 권한 확인
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.log('알림 권한이 거부되었습니다.');
      return null;
    }

    // Service Worker 등록 확인
    const registration = await navigator.serviceWorker.ready;
    if (!registration) {
      console.log('Service Worker가 등록되지 않았습니다.');
      return null;
    }

    // FCM 토큰 가져오기
    const currentToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });

    if (currentToken) {
      console.log('FCM 토큰:', currentToken);

      // Firestore에 토큰 저장
      await saveTokenToFirestore(userId, currentToken);

      return currentToken;
    } else {
      console.log('토큰을 가져올 수 없습니다. 권한을 확인하세요.');
      return null;
    }
  } catch (error) {
    console.error('FCM 토큰 가져오기 오류:', error);
    return null;
  }
};

/**
 * FCM 토큰을 Firestore에 저장
 * @param {string} userId - 사용자 ID
 * @param {string} token - FCM 토큰
 */
const saveTokenToFirestore = async (userId, token) => {
  try {
    const tokenRef = doc(db, 'user_notification_tokens', userId);
    await setDoc(tokenRef, {
      userId,
      token,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });

    console.log('토큰이 Firestore에 저장되었습니다.');
  } catch (error) {
    console.error('토큰 저장 오류:', error);
  }
};

/**
 * 포그라운드 메시지 수신 리스너 설정
 * @param {Function} callback - 메시지 수신 시 호출될 콜백 함수
 */
export const onForegroundMessage = (callback) => {
  if (!messaging) {
    console.log('Firebase Messaging이 지원되지 않습니다.');
    return () => {};
  }

  const unsubscribe = onMessage(messaging, (payload) => {
    console.log('포그라운드 메시지 수신:', payload);

    // 알림 데이터 추출
    const { notification, data } = payload;

    if (notification) {
      // 브라우저 알림 표시 (포그라운드 상태일 때)
      const notificationTitle = notification.title || '원오디너리맨션';
      const notificationOptions = {
        body: notification.body || '새로운 알림이 있습니다.',
        icon: notification.icon || '/logo192.png',
        badge: '/logo192.png',
        data: data || {}
      };

      new Notification(notificationTitle, notificationOptions);
    }

    // 콜백 함수 호출 (앱 내 알림 업데이트 등)
    if (callback && typeof callback === 'function') {
      callback(payload);
    }
  });

  return unsubscribe;
};

/**
 * 알림 권한 상태 확인
 * @returns {string} 'granted', 'denied', 'default', 또는 'unsupported'
 */
export const getNotificationPermission = () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
};

/**
 * 브라우저 알림 지원 여부 확인
 * @returns {boolean}
 */
export const isNotificationSupported = () => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};
